// Field catalogue for an App record (GET/PATCH sortVoter/<uuid>), shared by
// the Add/Edit App modal (AddApp.js) and the delete confirmation (AppList.js).
//
// "core" fields are always shown in the form. "optional" fields are only
// shown when the record has a value for them (or the user turns on "Show
// empty fields"), so null/empty columns like extra8..extra19 don't clutter
// the form, while any column that does have data is always visible.

export const toLabel = (name) =>
  String(name)
    .replace(/_/g, " ")
    .replace(/(\D)(\d+)$/, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return String(value).trim() !== "";
};

// extra20 holds the app's admin password.
const EXTRA_LABELS = { extra20: "Admin Password" };
const EXTRA_FIELDS = Array.from({ length: 20 }, (_, i) => `extra${i + 1}`).filter((name) => name !== "extra20");

export const FORM_SECTIONS = [
  {
    id: "settings",
    label: "Campaign Settings",
    // banner_send_date / assembly_number / switches are rendered specially.
    optional: [{ name: "extra20", label: EXTRA_LABELS.extra20 }],
  },
  {
    id: "messages",
    label: "Messages",
    core: [
      { name: "error_message", label: "Error Message" },
      { name: "promotion_message", label: "Promotion Message" },
      { name: "message_one", label: "Message One" },
      { name: "message_two", label: "Message Two" },
    ],
    optional: [
      { name: "promotion_message_hindi", label: "Promotion Message (Hindi)", multiline: true },
      { name: "message_hindi", label: "Message (Hindi)" },
      { name: "message_english", label: "Message (English)" },
    ],
  },
  {
    id: "candidate",
    label: "Candidate & Election",
    optional: [
      { name: "candidate_name_english", label: "Candidate Name (English)" },
      { name: "candidate_name_hindi", label: "Candidate Name (Hindi)" },
      { name: "election_name_english", label: "Election Name (English)" },
      { name: "election_name_hindi", label: "Election Name (Hindi)" },
      { name: "election_date_english", label: "Election Date (English)" },
      { name: "election_date_hindi", label: "Election Date (Hindi)" },
      { name: "language_supported", label: "Languages Supported" },
    ],
  },
  {
    id: "links",
    label: "Social & Website Links",
    core: ["whatsapp_link", "facebook_link", "youtube_link", "insta_link", "x_link", "websites_link"].map(
      (name) => ({ name, label: toLabel(name), type: "url" })
    ),
  },
  {
    id: "extras",
    label: "Extra Fields",
    optional: EXTRA_FIELDS.map((name) => ({ name, label: toLabel(name) })),
  },
  { id: "media", label: "Media Uploads" },
];

// Every editable text field in the form (core + optional), by name.
export const TEXT_FIELDS = FORM_SECTIONS.flatMap((section) => [
  ...(section.core || []),
  ...(section.optional || []),
]);

// Upload inputs: `name` is the key the PATCH/POST expects, `current` is the
// key the GET returns the existing file under (they differ for some).
export const MEDIA_FIELDS = [
  { name: "banner_image", current: "banner_image", label: "Banner Upload", optionalUpload: true },
  { name: "banner_image_link", current: "banner_image_link", label: "Banner Image" },
  { name: "bottom_image_link", current: "bottom_image_link", label: "Bottom Image" },
  { name: "whatsapp_image_link", current: "whatsapp_image_link", label: "WhatsApp Image" },
  { name: "manifesto_image_link", current: "manifesto_file", label: "Manifesto" },
  { name: "ballot_image_link", current: "ballot_file", label: "Ballot" },
  { name: "program_image_link", current: "program_file", label: "Program" },
  { name: "extra_image_link1", current: "extra_file1", label: "Extra Image 1" },
  { name: "extra_image_link2", current: "extra_file2", label: "Extra Image 2" },
  { name: "extra_image_link3", current: "extra_file3", label: "Extra Image 3" },
  { name: "extra_image_link4", current: "extra_file4", label: "Extra Image 4" },
];
export const UPLOAD_FIELD_NAMES = MEDIA_FIELDS.map((field) => field.name);

// Files the API returns but the form has no upload for - shown when present.
export const READONLY_MEDIA_FIELDS = [
  { name: "dashboard_image1", label: "Dashboard Image 1" },
  { name: "dashboard_image2", label: "Dashboard Image 2" },
  { name: "dashboard_image3", label: "Dashboard Image 3" },
  { name: "dashboard_image4", label: "Dashboard Image 4" },
  { name: "app_qr", label: "App QR Code" },
  { name: "app_url", label: "App Setup File" },
];

// Read-only facts about the app shown at the top of Edit (when present).
export const APP_INFO_FIELDS = [
  { name: "app_name", label: "App Name" },
  { name: "party_name", label: "Party Name" },
  { name: "app_name_english", label: "App Name (English)" },
  { name: "app_name_hindi", label: "App Name (Hindi)" },
  { name: "created", label: "Created", date: true },
  { name: "modified", label: "Last Modified", date: true },
];

// Summary rows for the delete confirmation - only the ones with data.
export const DELETE_SUMMARY_FIELDS = [
  { name: "app_name", label: "App Name" },
  { name: "party_name", label: "Party Name" },
  { name: "assembly_number", label: "Assembly Number" },
  { name: "candidate_name_english", label: "Candidate" },
  { name: "election_name_english", label: "Election" },
  { name: "banner_send_date", label: "Banner Date" },
  { name: "extra20", label: EXTRA_LABELS.extra20 },
];

export const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(String(url || ""));

export const formatInfoValue = (value, { date } = {}) => {
  if (Array.isArray(value)) return value.join(", ");
  if (date) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  }
  return String(value);
};
